<template>
  <v-container>
    <employee-leaves-dashboard-summary-view
      :total-subordinates="subordinates.length.toString()"
      :total-annual-leave="totalAnnualLeaveInSelectedMonth.toString()"
      :total-sick-leave="totalSickLeaveInSelectedMonth.toString()"
      :total-other-leave="totalOtherLeaveInSelectedMonth.toString()"
    />
    <v-row no-gutters>
      <v-col cols="12">
        <v-card>
          <employee-leaves-dashboard-sidebar-filter
            ref="filter"
            @branches-filter="filterBranch"
            @departments-filter="filterDepartment"
          />
          <v-card-title
            class="justify-center tertiary lighten-3 pa-3"
          >
            <h3 class="title white--text ma-auto">        
              <v-btn   
                text
                icon           
                color="white"
                @click.stop="$refs.filter.drawer = true"
              >
                <v-icon 
                  v-if="isFiltering" 
                  medium
                >
                  filter_list_alt
                </v-icon>
                <v-icon 
                  v-else
                  medium
                >
                  filter_list
                </v-icon>
              </v-btn>        
              Leave Dashboard
            </h3>      
            <csv
              v-if="$hasPermissionTo(['generate leave dashboard report'])"
              :data="reportData"
              :name="reportName"
            >
              <v-btn
                color="primary"
                :disabled="filteredSubordinates.length<1"
              >
                Download
              </v-btn>
            </csv>
          </v-card-title>    
          <v-card-actions class="justify-center tertiary lighten-3 mx-0 pt-1 pb-2">
            <v-sheet
              class="mx-auto tertiary lighten-3"
              max-width="600"
            >
              <v-slide-group
                class="px-2"
                show-arrows
                center-active
                :value="currentMonthIndex"
                mandatory
                dark
              >
                <v-slide-item
                  v-for="(month, i) in months"
                  :key="i"
                  v-slot:default="{ active, toggle }"
                >            
                  <v-btn
                    class="mx-2"
                    :input-value="active"
                    active-class="white--text"
                    text
                    @click="toggle(); onMonthChanged(month);"
                  >
                    {{ `${month.name} ${month.year}` }}
                  </v-btn>
                </v-slide-item>
              </v-slide-group>
            </v-sheet>
          </v-card-actions>
          <v-data-table
            :headers="dateHeaders"
            :loading="loading"
            :items="filteredSubordinates"
            :search="search"
            :custom-sort="customSort"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            multi-sort
            item-key="employee_uid"
            :height="filteredSubordinates.length > 8 ? '480px' : ''"
            hide-default-footer
            fixed-header
            disable-pagination
            hide-default-header
          >
            <template v-slot:header="{ props: { headers } }">
              <thead class="v-data-table-header">
                <tr>
                  <th class="pa-1">
                    <v-text-field
                      v-model="search"
                      class="custom"
                      prepend-icon="mdi-magnify"
                      label="Search employees"
                      dense
                      single-line
                      hide-details
                    />
                  </th>
                  <v-tooltip
                    v-for="(header, i) in headers.slice(1, headers.length)"
                    :key="i"
                    max-width="250px"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <th       
                        :width="header.width"       
                        :class="[
                          {'sortable': header.sortable },
                          {'active': header.text == sortBy[0] },
                          'text-center',
                          isToday(header.text) ? 'light-blue lighten-4' : {'grey lighten-5': isDayExcluded(header.text)},
                        ]"
                        v-on="on"
                        @click="sort(header.value)"
                      >
                        <span
                          :class="[
                            {'red--text text--lighten-2': isHoliday(header.text)}, 
                            header.text == sortBy[0] ? 'body-2' : 'caption', 
                            'font-weight-medium'
                          ]"
                        >
                          {{ header.text }}
                        </span>
                      </th>
                    </template>
                    <v-card-subtitle class="py-1">
                      {{ getDayOfDate(header.text) }}
                    </v-card-subtitle>
                    <div v-if="isHoliday(header.text)">
                      <v-divider />
                      <v-card-subtitle class="font-weight-bold py-1">
                        {{ (isHoliday(header.text) || {}).name || '' }}
                      </v-card-subtitle>
                    </div>
                  </v-tooltip>
                </tr>
              </thead>
            </template>
            <template v-slot:item="{ item, headers }"> 
              <tr>
                <td>{{ item.person.name }}</td>
                <td
                  v-for="(header, i) in headers.slice(1, headers.length)"
                  :key="i"
                  :class="[
                    isToday(header.text) ? 'light-blue lighten-4' : {'grey lighten-5': isDayExcluded(header.text)}, 
                    'body-td', 'px-0'
                  ]"
                >
                  <v-tooltip
                    v-if="currentLeaveApplication(item.leave_applications, header.text)"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <div              
                        :class="[                          
                          {
                            'secondary': currentLeaveApplication(item.leave_applications, header.text).leave_entitlement.leave_type.name.toLowerCase().includes('annual')
                          },
                          {
                            'dark-red': currentLeaveApplication(item.leave_applications, header.text).leave_entitlement.leave_type.name.toLowerCase().includes('sick')
                          },
                          {
                            'div-on-leave': getCurrentLeaveApplicationSession(item.leave_applications, header.text).leave_session_id === 4
                          },
                          '  text-center'
                        ]"
                        v-on="on"
                      >
                        <span>
                          <v-icon
                            v-if="getCurrentLeaveApplicationSession(item.leave_applications, header.text).leave_session_id === 2"
                            small
                            color="yellow"
                          >
                            fa fa-sun-o
                          </v-icon>
                          <v-icon
                            v-if="getCurrentLeaveApplicationSession(item.leave_applications, header.text).leave_session_id === 3"
                            small
                            color="yellow"
                          >
                            fa fa-moon-o
                          </v-icon>
                      
                        </span>
                      </div>
                    </template>
                    <span>{{ currentLeaveApplication(item.leave_applications, header.text).leave_entitlement.leave_type.name }}</span>
                  </v-tooltip>            
                </td>
              </tr>
            </template>
            <template v-slot:body.append="{ headers }">
              <tr>
                <td
                  class="footer-td text-right"
                  :width="headers[0].width"
                >
                  <span class="font-weight-medium">Total on leave</span>
                </td>
                <td
                  v-for="(header, i) in headers.slice(1, headers.length)"
                  :key="i"
                  class="footer-td text-center"
                  :width="header.width"
                >
                  <span class="font-weight-medium">{{ getTotalOnLeave(header.text) }}</span>        
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import Csv from 'vue-json-csv'
  export default {
    components: {
      EmployeeLeavesDashboardSummaryView: () => import('@/components/employee/leaves/dashboard/SummaryView'),
      EmployeeLeavesDashboardSidebarFilter: () => import('@/components/employee/leaves/dashboard/SidebarFilter'),
      Csv,
    },
    data () {
      return {
        search: '',
        loading: false,
        sortBy: [this.$moment().get('date').toString(), 'person.name'],
        sortDesc: [false, false],
        subordinates: [],
        holidayDetails: [],
        currentMonthIndex: this.$moment().month(),
        selectedMonth: this.$moment().month() + 1,
        selectedYear: this.$moment().year(),
        headers: [
          {
            text: '',
            sortable: false,
            filterable: true,
            value: 'person.name',
            width: '10%',
          },
        ],
        filters: {
          branch: '',
          department: '',
        },
      }
    },
    computed: {   
      reportName () {
        return 'Monthly Report(' + this.$moment(this.selectedYearMonth).format('MMM YYYY') + ').csv'
      },
      allLeaveDays () {
        return this.filteredSubordinates.reduce((acc1, subordinate) => {
          return acc1.concat(
            subordinate.leave_applications.reduce((acc2, leaveApplication) => {
              return acc2.concat(
                leaveApplication.leave_days.map(leaveDay => leaveDay.date)
              )
            }, [])
          )          
        }, [])
      },   
      dateHeaders () {
        let headersCopy = [...this.headers]
        const daysInMonth = this.$moment(`${this.selectedYearMonth}`).daysInMonth()
        const headerWidth = (90 / daysInMonth)

        for (let i = 0; i < daysInMonth; i++) {
          const day = i + 1

          headersCopy.push({
            text: day,
            sortable: true,
            filterable: false,
            value: `${day}`,
            width: `${headerWidth}%`,
          })
        } 
      
        return headersCopy
      },
      reportData () {
        if (!this.dateHeaders || !this.filteredSubordinates) return []
        let result = []

        const daysInMonth = this.$moment(`${this.selectedYearMonth}`).daysInMonth()
        
        this.filteredSubordinates.forEach((subordinate, index) => {
          result.push({ Name: subordinate.person.name })
        
          for (let i = 1; i <= daysInMonth; i++) {
            result[index]['Day-' + i] = this.currentLeaveApplication(subordinate.leave_applications, i) 
              ? (((this.currentLeaveApplication(subordinate.leave_applications, i) || {}).leave_entitlement || {}).leave_type || {}).name +
                '(' + this.reportLeaveSession(this.reportLeaveType(this.currentLeaveApplication(subordinate.leave_applications, i), i)) +
                ')' : null
          }
        })
        return result
      },

      totalAnnualLeaveInSelectedMonth () {
        return this.getTotalLeaveApplications(`${this.selectedYearMonth}-01`, 'annual') || 0
      },
      totalSickLeaveInSelectedMonth () {
        return this.getTotalLeaveApplications(`${this.selectedYearMonth}-01`, 'sick') || 0
      },
      totalOtherLeaveInSelectedMonth () {
        return this.getTotalLeaveApplications(`${this.selectedYearMonth}-01`, 'others', ['annual', 'sick']) || 0
      },
      filteredSubordinates () {
        return this.subordinates
          .filter(this.filter)
      },
      isFiltering () {
        return !_.isEmpty(this.filters.branch) ||
          !_.isEmpty(this.filters.department)
      },
      holidayDates () {
        return this.holidayDetails.map(holidayDetail => {
          return { name: holidayDetail.holiday.name, date: holidayDetail.date }
        })
      },
      selectedYearMonth () {
        return `${this.selectedYear}-${this.selectedMonth}`
      },
      months () {
        return this.getMonthsOfYear(this.currentYear).concat(this.getMonthsOfYear(this.nextYear))
      },  
      currentYear () {
        return this.$moment().year()
      },
      nextYear () {
        return this.$moment().add(1, 'year').year()
      },
    },
    created () {
      this.loadSubordinates()
      this.loadHolidays()
    },
    methods: {      
      customSort (items, sortBy, sortDesc) {
        return items.sort((a, b) => {
          if (!sortDesc[0]) {           
            // if a bigger than b, move a up
            if ((this.currentLeaveApplication(a.leave_applications, sortBy[0]) ? 1 : 0) > 
              (this.currentLeaveApplication(b.leave_applications, sortBy[0]) ? 1 : 0)) return -1

            // if a smaller than b, move a down
            if ((this.currentLeaveApplication(a.leave_applications, sortBy[0]) ? 1 : 0) < 
              (this.currentLeaveApplication(b.leave_applications, sortBy[0]) ? 1 : 0)) return 1
          } else {           
            // if a bigger than b, move a down
            if ((this.currentLeaveApplication(a.leave_applications, sortBy[0]) ? 1 : 0) > 
              (this.currentLeaveApplication(b.leave_applications, sortBy[0]) ? 1 : 0)) return 1

            // if a smaller than b, move a up
            if ((this.currentLeaveApplication(a.leave_applications, sortBy[0]) ? 1 : 0) < 
              (this.currentLeaveApplication(b.leave_applications, sortBy[0]) ? 1 : 0)) return -1
          }

          const nameA = a.person.name.toUpperCase()
          const nameB = b.person.name.toUpperCase()

          // if name A comes first in the alphabet, move A up
          if (nameA < nameB) return -1

          // else move A down
          if (nameA > nameB) return 1

          return 0
        })
      },
      isDayExcluded (day) {
        const date = `${this.selectedYearMonth}-${day}`

        return this.$moment(date).day() === 0 || this.isHoliday(day)
      },
      loadSubordinates () {
        this.loading = true

        this.$api.subordinateWithLeaves().list()
          .then(response => {
            this.subordinates = response.data

            this.loading = false
          })
      },
      loadHolidays () {
        this.$api.holidayDetails().list('?filter[year]=' + new Date().getFullYear())
          .then(response => {
            this.holidayDetails = response.data
          })
      },      
      sort (val) {
        this.$set(this.sortBy, 0, val)
        this.$set(this.sortDesc, 0, !this.sortDesc[0])
      },
      filterBranch (val) {
        this.filters = Object.assign({}, this.filters, { branch: val })
      },
      filterDepartment (val) {
        this.filters = Object.assign({}, this.filters, { department: val })
      },
      filter (subordinate) {
        if (!this.isFiltering) return subordinate
  
        const branches = subordinate.user.active_branches.map(branch => branch.branch_uid)

        return (this.filters.branch.length === 0 ? true : _.intersection(branches, this.filters.branch).length > 0) &&
          (this.filters.department.length === 0 ? true : this.filters.department.includes(subordinate.department_id))
      },
      currentLeaveApplication (leaveApplications, day) {
        return leaveApplications.find(leaveApplication => {
          return leaveApplication.leave_days.some(leaveDay => {
            return this.$moment(`${this.selectedYearMonth}-${day}`).isSame(leaveDay.date)
          })
        })        
      },      
      getCurrentLeaveApplicationSession (leaveApplications, day) {
        let obj = {}    

        leaveApplications.map(leaveApplication => {
          leaveApplication.leave_days.map((leaveDay, index) => {
            if (this.$moment(`${this.selectedYearMonth}-${day}`).format('YYYY-MM-DD') === leaveDay.date) {
              obj = leaveDay
            }
          })
        })
     
        return obj
      },
      getTotalLeaveApplications (selectedDate, type, excludes = []) {
        return this.subordinates.reduce((accumulator, subordinate) => {
          return accumulator + subordinate.leave_applications.filter(leaveApplication => {
            return (this.$moment(leaveApplication.start_date).isSame(selectedDate, 'month') || this.$moment(leaveApplication.end_date).isSame(selectedDate, 'month')) && 
              (excludes.length > 0 
                ? !excludes.some(exclude => leaveApplication.leave_entitlement.leave_type.name.toLowerCase().includes(exclude))
                : leaveApplication.leave_entitlement.leave_type.name.toLowerCase().includes(type.toLowerCase()))
          }).length
        }, 0)
      },
      getTotalOnLeave (day) {
        const date = `${this.selectedYearMonth}-${day}`

        return this.allLeaveDays.filter(leaveDay => {
          return this.$moment(date).isSame(leaveDay)
        }).length
      },
      getDayOfDate (day) {
        const date = `${this.selectedYearMonth}-${day}`

        return this.$moment(date).format('ddd')
      },
      isHoliday (day) {
        const date = `${this.selectedYearMonth}-${day}`

        return this.holidayDates.find(holidayDate => holidayDate.date === this.$moment(date).format('YYYY-MM-DD'))
      },
      isToday (day) {
        const date = `${this.selectedYearMonth}-${day}`

        return this.$moment(date).isSame(this.$moment().format('YYYY-MM-DD'))
      },
      getMonthsOfYear (year) {
        return this.$moment.monthsShort().map(month => {
          return {
            name: month,
            year: year,
          }
        })
      },
      onMonthChanged (month) {
        this.selectedMonth = this.$moment().month(month.name).month() + 1
        this.selectedYear = month.year
      },
      reportLeaveType (leaveApplications, day) {
        let obj = {}    
        if (leaveApplications) {
          if (day < 10) {
            day = '0' + day
          }
       
          leaveApplications.leave_days.map((leaveDay, index) => {
            if (this.$moment(`${this.selectedYearMonth}-${day}`).format('YYYY-MM-DD') === leaveDay.date) {
              obj = leaveDay
            }
          }) 
        }
        return obj
      },

      reportLeaveSession (obj) {
        if (obj.leave_session_id === 2) {
          return 'AM'
        } else if (obj.leave_session_id === 3) {
          return 'PM'
        }
        return 'FULL'
      },
    },
  }
</script>

<style scoped>
  .dark-red {
    background: #FC2750 !important;
  }
  .v-data-table .v-data-table-header th.sortable.active {
    color: rgb(0, 0, 0);
  }

  .body-td {
    border-left: 1px solid rgba(0,0,0,.04);
  }

  .footer-td {
    border-top: 1px solid rgba(0,0,0,.04);
    border-left: 1px solid rgba(0,0,0,.04);
  }

  .v-data-table--fixed-header tr .footer-td{
    background: #FFFFFF;
    position: sticky;
    bottom:0;
  }

  .div-on-leave {
    background: #ffa21a;
    padding:10.5px 0px;
  }
</style>
